/**
 * Party Service - API client for party (customer/vendor) management.
 */

import type { Party, CreatePartyRequest, UpdatePartyRequest, Address, ListPartiesResponse, GetNextPartyCodeResponse } from '@/gen/proto'

import { get, post, patch, put, buildQueryString, type ApiResponse } from './api-client'
import { searchByIdentifiers, getEntity, type Entity } from './entity-service'
import { ROLE_CUSTOMER, ROLE_SUPPLIER } from '../types/billing'
import { firstNonBlank } from '../utils/formatters'

import type { PartyListFilters } from '../types/billing'


export type { Party, CreatePartyRequest, UpdatePartyRequest }
export type PartyListResponse = ListPartiesResponse

export async function checkDuplicateEntity(identifiers: {
  gstin?: string; pan?: string; cin?: string; tan?: string; phone?: string; email?: string
}): Promise<ApiResponse<Entity[]>> {
  return searchByIdentifiers(identifiers)
}

/** Get code prefix from roles: C = customer-only, V = supplier-only, P = both/other */
function getRolesPrefix(roles: string[]): string {
  const isCustomer = roles.includes(ROLE_CUSTOMER)
  const isSupplier = roles.includes(ROLE_SUPPLIER)
  if (isCustomer && !isSupplier) { return 'C' }
  if (isSupplier && !isCustomer) { return 'V' }
  return 'P'
}

/**
 * Client-side next-code guess. Kept only as an offline/error fallback and for
 * instant display; the server (fetchNextPartyCode / createParty) is authoritative.
 */
export function getNextPartyCode(roles: string[], existingCodes: string[]): string {
  const prefix = getRolesPrefix(roles)
  /* v8 ignore next */
  const numbers = existingCodes.filter(c => c.startsWith(prefix)).map(c => parseInt(c.substring(1)) ?? 0)
  const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1
  return `${prefix}${String(next).padStart(3, '0')}`
}

/**
 * Fetch the next available party code from the server. The roles determine the
 * prefix (C/V/P). This is the authoritative source for prefilling the create
 * form — it computes the next number in SQL rather than scanning a client list.
 */
export async function fetchNextPartyCode(orgId: string, ledgerId: string, roles: string[]): Promise<ApiResponse<string>> {
  const params = new URLSearchParams()
  roles.forEach(r => { if (r) { params.append('roles', r) } })
  const qs = params.toString()
  const res = await get<GetNextPartyCodeResponse>(
    `/organizations/${orgId}/ledgers/${ledgerId}/parties/next-code${qs ? `?${qs}` : ''}`,
  )
  if (res.error) { return { error: res.error } }
  return { data: res.data?.code ?? '' }
}

interface ListPartiesOptions {
  orgId: string; ledgerId: string; filters?: PartyListFilters; pageSize?: number; pageToken?: string
}

export async function listParties(opts: ListPartiesOptions): Promise<ApiResponse<PartyListResponse>> {
  const { orgId, ledgerId, filters, pageSize, pageToken } = opts
  const query = buildQueryString({
    pageSize, pageToken, role: filters?.role,
    activeOnly: filters?.activeOnly, query: filters?.query, enrich: true,
  })
  return get<PartyListResponse>(`/organizations/${orgId}/ledgers/${ledgerId}/parties${query}`)
}

interface ListAllPartiesOptions {
  orgId: string; filters?: PartyListFilters; pageSize?: number; pageToken?: string
  /**
   * Whether to enrich each party with entity name/tradeName (default true).
   * Callers that only need counts (e.g. header stat "peek" queries — see
   * entities-helpers.ts's statCountFromPeek) should pass false to avoid
   * paying for enrichment on rows whose fields are never rendered.
   */
  enrich?: boolean
}

/** List all parties across ledgers for an organization. */
export async function listAllParties(opts: ListAllPartiesOptions): Promise<ApiResponse<PartyListResponse>> {
  const { orgId, filters, pageSize, pageToken, enrich: shouldEnrich = true } = opts
  const query = buildQueryString({
    pageSize, pageToken, role: filters?.role,
    activeOnly: filters?.activeOnly, query: filters?.query, enrich: shouldEnrich,
  })
  return get<PartyListResponse>(`/organizations/${orgId}/parties${query}`)
}

export async function getParty(orgId: string, partyId: string): Promise<ApiResponse<Party>> {
  return get<Party>(`/organizations/${orgId}/parties/${partyId}${buildQueryString({ enrich: true })}`)
}

function buildCreatePartyRequest(data: CreatePartyRequest): Record<string, unknown> {
  const req: Record<string, unknown> = {
    entityId: data.entityId, code: data.code, name: data.name, tradeName: data.tradeName,
    roles: data.roles, partyType: data.partyType,
    gstRegistrationType: data.gstRegistrationType,
    gstin: data.gstin,
    contact: data.contact,
    businessIdentifiers: data.businessIdentifiers,
    billingAddress: data.billingAddress, shippingAddress: data.shippingAddress,
    billingAddressId: data.billingAddressId, shippingAddressId: data.shippingAddressId,
    creditDays: data.creditDays, creditLimit: data.creditLimit,
    receivableAccountId: data.receivableAccountId, payableAccountId: data.payableAccountId,
    // Currency is mandatory — never persist a blank/whitespace value. Omit it
    // when empty so the server applies its default instead of storing garbage.
    defaultCurrency: firstNonBlank(data.defaultCurrency),
    companyVendorCode: data.companyVendorCode,
  }
  Object.keys(req).forEach(k => { if (req[k] === undefined) { delete req[k] } })
  return req
}

export async function createParty(orgId: string, ledgerId: string, data: CreatePartyRequest): Promise<ApiResponse<Party>> {
  return post<Party>(`/organizations/${orgId}/ledgers/${ledgerId}/parties`, buildCreatePartyRequest(data))
}

function buildContactObject(data: UpdatePartyRequest): Record<string, string> | undefined {
  // Contact object is already in the correct format, just pass it through as Record<string, string>
  return data.contact as Record<string, string> | undefined
}

function safeStr(val: string | undefined): string { return val ?? '' }

function mapAddress(addr: Address): Record<string, unknown> {
  // The Go server matches billing/shipping addresses by type or label.
  // If the frontend has a usage array (e.g. ['billing', 'shipping']), use the
  // primary purpose as the type so the Go server can match it correctly.
  const usage = (addr as Address & { usage?: string[] }).usage ?? []
  const primaryPurpose = usage.find(u => u === 'billing' || u === 'shipping' || u === 'consignee') ?? null
  return {
    id: safeStr(addr.id),
    label: addr.label ?? primaryPurpose ?? '',
    type: primaryPurpose ?? addr.type ?? 'office',
    line1: addr.line1, line2: safeStr(addr.line2), city: addr.city, state: safeStr(addr.state), stateCode: safeStr(addr.stateCode),
    /* v8 ignore next */
    postalCode: addr.postalCode, country: addr.country, countryCode: addr.countryCode ?? 'IN',
    gstin: safeStr(addr.gstin), companyCode: safeStr(addr.companyCode), isPrimary: addr.isPrimary ?? false,
  }
}

/** Fetch all entity addresses, falling back to the party's proto-level fields. */
async function fetchAllEntityAddresses(party: Party): Promise<Address[]> {
  if (party.entityId) {
    try {
      const entityResp = await getEntity(party.entityId)
      if (entityResp.data?.addresses && entityResp.data.addresses.length > 0) {
        return entityResp.data.addresses.map(ea => ({
          id: ea.id ?? '',
          label: ea.label,
          type: ea.type,
          usage: ea.usage ?? [],
          line1: ea.line1, line2: ea.line2,
          city: ea.city, state: ea.state ?? '',
          stateCode: ea.stateCode, postalCode: ea.postalCode,
          country: ea.country, countryCode: ea.countryCode,
          gstin: ea.gstin ?? '', companyCode: ea.companyCode ?? '',
          isPrimary: ea.isPrimary ?? false,
        } as Address))
      }
    } catch { /* fall through to proto fields */ }
  }
  const fallback: Address[] = []
  if (party.billingAddress) { fallback.push(party.billingAddress) }
  if (party.shippingAddress) { fallback.push(party.shippingAddress) }
  return fallback
}

function setIfDefined<T>(req: Record<string, unknown>, key: string, value: T | undefined): void {
  if (value !== undefined) { req[key] = value }
}

function buildUpdateRequest(data: UpdatePartyRequest): Record<string, unknown> {
  const req: Record<string, unknown> = {}
  setIfDefined(req, 'name', data.name)
  setIfDefined(req, 'tradeName', data.tradeName)
  // Note: gstin is now part of business_identifiers in proto
  setIfDefined(req, 'businessIdentifiers', data.businessIdentifiers)
  const contact = buildContactObject(data)
  if (contact) { req.contact = contact }
  // Proto UpdatePartyRequest supports addresses array for entity service updates
  if (data.addresses && data.addresses.length > 0) { req.addresses = data.addresses.map(mapAddress) }
  setIfDefined(req, 'creditDays', data.creditDays)
  setIfDefined(req, 'creditLimit', data.creditLimit)
  // Currency is set-once on the server: sent only to assign one to a currency-less
  // party. Omit blanks so an established currency is never touched. firstNonBlank
  // collapses '' / whitespace to undefined, which setIfDefined then skips.
  setIfDefined(req, 'defaultCurrency', firstNonBlank(data.defaultCurrency))
  setIfDefined(req, 'companyVendorCode', data.companyVendorCode)
  setIfDefined(req, 'billingAddressId', data.billingAddressId)
  setIfDefined(req, 'shippingAddressId', data.shippingAddressId)
  setIfDefined(req, 'isActive', data.isActive)
  return req
}

export async function updateParty(orgId: string, partyId: string, data: UpdatePartyRequest): Promise<ApiResponse<Party>> {
  return patch<Party>(`/organizations/${orgId}/parties/${partyId}`, buildUpdateRequest(data))
}

export async function setPartyRoles(orgId: string, partyId: string, roles: string[]): Promise<ApiResponse<void>> {
  return put<void>(`/organizations/${orgId}/parties/${partyId}/roles`, { roles })
}

export async function ensurePartyAccount(
  orgId: string,
  partyId: string,
  purpose: string,
  role?: string,
): Promise<ApiResponse<{ accountId: string; role: string; purpose: string }>> {
  return post(`/organizations/${orgId}/parties/${partyId}/accounts/ensure`, { purpose, role })
}

export interface PartyRoleAccountItem {
  role: string
  roleLabel: string
  purpose: string
  purposeLabel: string
  accountId?: string
  accountCode?: string
  accountName?: string
}

export async function listPartyRoleAccounts(
  orgId: string,
  partyId: string,
): Promise<ApiResponse<{ roleAccounts: PartyRoleAccountItem[] }>> {
  return get(`/organizations/${orgId}/parties/${partyId}/role-accounts`)
}

/**
 * Creates (or returns the existing) GL subledger account for a party in the
 * given role, auto-assigning the role to the party if it doesn't already
 * hold it. Backs the Entity Detail "create account for role" action — the
 * server derives the parent/code/name from the role's own account mapping,
 * via the same path invoice/bill posting uses.
 */
export async function createPartyRoleAccount(
  orgId: string,
  partyId: string,
  role: string,
): Promise<ApiResponse<PartyRoleAccountItem>> {
  return post(`/organizations/${orgId}/parties/${partyId}/role-accounts`, { role })
}

export async function deactivateParty(orgId: string, partyId: string): Promise<ApiResponse<Party>> {
  return post<Party>(`/organizations/${orgId}/parties/${partyId}:deactivate`)
}

export async function reactivateParty(orgId: string, partyId: string): Promise<ApiResponse<Party>> {
  return post<Party>(`/organizations/${orgId}/parties/${partyId}:reactivate`)
}

interface AddressUpdateOptions { orgId: string; partyId: string; party: Party }

export async function addPartyAddress(opts: AddressUpdateOptions, address: Address): Promise<ApiResponse<Party>> {
  const existing = await fetchAllEntityAddresses(opts.party)
  return updateParty(opts.orgId, opts.partyId, { addresses: [...existing, address] } as UpdatePartyRequest)
}

export async function updatePartyAddress(opts: AddressUpdateOptions, addressId: string, updated: Address): Promise<ApiResponse<Party>> {
  const existing = await fetchAllEntityAddresses(opts.party)
  const addresses = existing.map(a => a.id === addressId ? { ...updated, id: addressId } : a)
  return updateParty(opts.orgId, opts.partyId, { addresses } as UpdatePartyRequest)
}

export async function deletePartyAddress(opts: AddressUpdateOptions, addressId: string): Promise<ApiResponse<Party>> {
  const existing = await fetchAllEntityAddresses(opts.party)
  const addresses = existing.filter(a => a.id !== addressId)
  return updateParty(opts.orgId, opts.partyId, { addresses } as UpdatePartyRequest)
}

// ── Scoring ───────────────────────────────────────────────────────────────────

export interface DimensionScore {
  key: string
  score: number
  max: number
  percentile?: number
  detail: string
}

export interface ScoreAlert {
  severity: 'warning' | 'info'
  code: string
  message: string
}

export interface PartyScore {
  partyId: string
  total: number
  tier: string
  dimensions: DimensionScore[]
  alerts: ScoreAlert[]
  computedAt: string
  manualRating?: number
}

export interface ScoringWeights {
  volume: number
  discipline: number
  frequency: number
  credit: number
  growth: number
  manual: number
}

export interface ScoringTiers {
  a: number
  b: number
  c: number
  d: number
}

export interface ScoringAlerts {
  overdueDays: number
  dormantDays: number
  creditUtilPct: number
}

export interface OrgScoringConfig {
  id?: string
  orgId: string
  weights: ScoringWeights
  tiers: ScoringTiers
  alerts: ScoringAlerts
  updatedAt?: string
  updatedBy?: string
}

export async function getPartyScore(orgId: string, partyId: string): Promise<ApiResponse<PartyScore>> {
  return get<PartyScore>(`/organizations/${orgId}/parties/${partyId}/score`)
}

export async function batchGetPartyScores(orgId: string, partyIds: string[]): Promise<ApiResponse<{ scores: Record<string, PartySummaryScore> }>> {
  return post<{ scores: Record<string, PartySummaryScore> }>(`/organizations/${orgId}/parties/scores`, { partyIds })
}

export interface PartySummaryScore {
  total: number
  tier: string
}

export async function setPartyManualRating(orgId: string, partyId: string, rating: number | null, notes: string): Promise<ApiResponse<void>> {
  return patch<void>(`/organizations/${orgId}/parties/${partyId}/rating`, { rating, notes })
}

export async function getOrgScoringConfig(orgId: string): Promise<ApiResponse<OrgScoringConfig>> {
  return get<OrgScoringConfig>(`/organizations/${orgId}/settings/entity-scoring`)
}

export async function saveOrgScoringConfig(orgId: string, cfg: Omit<OrgScoringConfig, 'id' | 'orgId' | 'updatedAt' | 'updatedBy'>): Promise<ApiResponse<void>> {
  return put<void>(`/organizations/${orgId}/settings/entity-scoring`, cfg)
}
